import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Joyride, STATUS, ACTIONS, EVENTS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

// Indeks for «La oss gå til profilen din»-steget – etter dette navigerer touren
// til /profil/{brukernavn} og fortsetter med reelle elementer på profilsiden.
const PROFILE_TRANSITION_INDEX = 11;

const buildSteps = (t) => [
  {
    target: "body",
    placement: "center",
    title: t("onboarding_tour.s0_t"),
    content: t("onboarding_tour.s0_c"),
    skipBeacon: true,
  },
  {
    target: "[data-tour='nav-forum']",
    title: t("onboarding_tour.s1_t"),
    content: t("onboarding_tour.s1_c"),
  },
  {
    target: "[data-tour='nav-bilder']",
    title: t("onboarding_tour.s2_t"),
    content: t("onboarding_tour.s2_c"),
  },
  {
    target: "[data-tour='nav-noveller']",
    title: t("onboarding_tour.s3_t"),
    content: t("onboarding_tour.s3_c"),
  },
  {
    target: "[data-tour='nav-grupper']",
    title: t("onboarding_tour.s4_t"),
    content: t("onboarding_tour.s4_c"),
  },
  {
    target: "[data-tour='nav-annonser']",
    title: t("onboarding_tour.s5_t"),
    content: t("onboarding_tour.s5_c"),
  },
  {
    target: "[data-tour='nav-sok']",
    title: t("onboarding_tour.s6_t"),
    content: t("onboarding_tour.s6_c"),
  },
  {
    target: "[data-tour='nav-skapere']",
    title: t("onboarding_tour.s7_t"),
    content: t("onboarding_tour.s7_c"),
  },
  {
    target: "[data-tour='nav-konkurranser']",
    title: t("onboarding_tour.s8_t"),
    content: t("onboarding_tour.s8_c"),
  },
  {
    target: "[data-tour='wallet']",
    title: t("onboarding_tour.s9_t"),
    content: t("onboarding_tour.s9_c"),
  },
  {
    target: "[data-tour='notifs']",
    title: t("onboarding_tour.s10_t"),
    content: t("onboarding_tour.s10_c"),
  },
  {
    target: "[data-tour='profile-menu']",
    title: t("onboarding_tour.s11_t"),
    content: t("onboarding_tour.s11_c"),
  },
  // Index 11 – etter dette steget navigerer touren til /profil/{brukernavn}
  {
    target: "body",
    placement: "center",
    title: t("onboarding_tour.s12_t"),
    content: t("onboarding_tour.s12_c"),
    skipBeacon: true,
  },
  // Steg som vises PÅ profilsiden
  {
    target: "[data-testid='profile-username']",
    title: t("onboarding_tour.s13_t"),
    content: t("onboarding_tour.s13_c"),
  },
  {
    target: "[data-testid='edit-profile-button']",
    title: t("onboarding_tour.s14_t"),
    content: t("onboarding_tour.s14_c"),
  },
  {
    target: "[data-testid='tab-galleri']",
    title: t("onboarding_tour.s15_t"),
    content: t("onboarding_tour.s15_c"),
  },
  {
    target: "[data-testid='tab-noveller']",
    title: t("onboarding_tour.s16_t"),
    content: t("onboarding_tour.s16_c"),
  },
  {
    target: "[data-testid='tab-gjestebok']",
    title: t("onboarding_tour.s17_t"),
    content: t("onboarding_tour.s17_c"),
  },
  {
    target: "[data-testid='tab-innstillinger']",
    title: t("onboarding_tour.s18_t"),
    content: t("onboarding_tour.s18_c"),
  },
  {
    target: "body",
    placement: "center",
    title: t("onboarding_tour.s19_t"),
    content: t("onboarding_tour.s19_c"),
    skipBeacon: true,
  },
];

export const STYLES = {
  arrow: { color: "#0A0A0A" },
  tooltip: {
    backgroundColor: "#0A0A0A",
    border: "1px solid rgba(242, 235, 224, 0.2)",
    borderRadius: 0,
    padding: 24,
    color: "#F2EBE0",
    maxWidth: 380,
  },
  tooltipTitle: {
    fontFamily: "'Fraunces', serif",
    fontStyle: "italic",
    fontSize: 22,
    color: "#F2EBE0",
    marginBottom: 8,
  },
  tooltipContent: {
    fontFamily: "'Manrope', sans-serif",
    fontSize: 14,
    lineHeight: 1.6,
    color: "rgba(242, 235, 224, 0.85)",
    padding: 0,
  },
  buttonPrimary: {
    backgroundColor: "#D11A5B",
    color: "#0A0A0A",
    fontFamily: "monospace",
    fontSize: 11,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    borderRadius: 0,
    padding: "10px 18px",
  },
  buttonBack: {
    color: "rgba(242, 235, 224, 0.7)",
    fontFamily: "monospace",
    fontSize: 11,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    marginRight: 8,
  },
  buttonSkip: {
    color: "rgba(242, 235, 224, 0.5)",
    fontFamily: "monospace",
    fontSize: 11,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
  },
  buttonClose: { display: "none" },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
};

// Standard-opsjoner som gjelder for alle steg
export const DEFAULT_OPTIONS = {
  buttons: ["back", "primary", "skip"],
  showProgress: true,
  overlayClickAction: false,
  primaryColor: "#D11A5B",
  textColor: "#F2EBE0",
  backgroundColor: "#0A0A0A",
  arrowColor: "#0A0A0A",
  zIndex: 10000,
  // Gi profilsiden tid til å montere når touren navigerer dit
  targetWaitTimeout: 5000,
  // Header er sticky (h-20 = 80px). Scroll målene godt under den slik at de
  // ikke havner bak headeren.
  scrollOffset: 120,
};

export default function OnboardingTour({ forceStart = false, onClose }) {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const steps = buildSteps(t);

  useEffect(() => {
    if (forceStart) { setStepIndex(0); setRun(true); return; }
    if (user && user.onboarding_seen === false) {
      const t = setTimeout(() => { setStepIndex(0); setRun(true); }, 1200);
      return () => clearTimeout(t);
    }
  }, [user, forceStart]);

  const finish = async () => {
    setRun(false);
    setStepIndex(0);
    try {
      await api.post("/me/onboarding-seen");
      if (refresh) await refresh();
    } catch {}
    onClose && onClose();
  };

  const handleEvent = async (data) => {
    const { status, type, index, action } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      await finish();
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const goingForward = action !== ACTIONS.PREV;
      setStepIndex(goingForward ? index + 1 : Math.max(index - 1, 0));
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const goingForward = action !== ACTIONS.PREV;
      const next = goingForward ? index + 1 : index - 1;

      if (
        goingForward &&
        index === PROFILE_TRANSITION_INDEX &&
        user?.username
      ) {
        // Naviger til profilsiden – Joyride venter på neste target via targetWaitTimeout
        navigate(`/profile/${user.username}`);
        setStepIndex(next);
        return;
      }

      setStepIndex(next);
    }
  };

  if (!user) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={DEFAULT_OPTIONS}
      styles={STYLES}
      locale={{
        back: t("beta_tour.back"), close: t("beta_tour.close"), last: t("beta_tour.last"),
        next: t("beta_tour.next"), nextWithProgress: t("beta_tour.progress"),
        open: t("beta_tour.open"), skip: t("beta_tour.skip"),
      }}
    />
  );
}
